<template>
  <el-dialog id="bankCardEditor" :title="title" :visible="show" width="600px" :close-on-click-modal="false" @open="handleDialogOpen" @close="handleDialogClose">
    <el-form :model="form" :rules="rules" ref="form" label-width="130px">
      <el-divider content-position="left">{{ $t('text.cardInformation') }}</el-divider>
      <el-form-item prop="name" :label="$t('text.cardName')">
        <el-input v-model="form.name" auto-complete="off" :placeholder="$t('message.inputCardName')"/>
      </el-form-item>
      <el-form-item prop="bankCode" :label="$t('text.bankName')">
        <el-select v-model="form.bankCode" filterable :placeholder="$t('message.selectBankName')" :disabled="editMode">
          <el-option v-for="bank in bankList" :key="bank.value" :label="bank.name" :value="bank.value"/>
        </el-select>
      </el-form-item>
      <el-form-item prop="accountNumber" :label="$t('text.accountNumber')">
        <el-input v-model="form.accountNumber" auto-complete="off" :placeholder="$t('message.inputAccountNumber')" :disabled="editMode"/>
      </el-form-item>
      <el-form-item prop="iban" :label="$t('text.iban')">
        <el-input v-model="form.iban" auto-complete="off" :placeholder="$t('message.inputIban')" :disabled="editMode"/>
      </el-form-item>
      <el-form-item prop="groups" :label="$t('text.groups')">
        <el-select v-model="form.groups" multiple :placeholder="$t('message.selectGroup')" :disabled="groupsDisabled">
          <el-option-group :key="$t('text.normalGroup')" :label="$t('text.normalGroup')">
            <el-option v-for="group in groupList" :key="group.value" :label="group.name" :value="group.value"/>
          </el-option-group>
          <el-option-group :key="$t('text.specialGroup')" :label="$t('text.specialGroup')">
            <el-option :key="-1" :label="$t('text.trash')" :value="-1" :disabled="true"/>
            <el-option :key="-2" :label="$t('text.backup')" :value="-2" :disabled="true"/>
          </el-option-group>
        </el-select>
      </el-form-item>
      <el-form-item prop="specialGroup" :label="$t('text.specialGroup')">
        <el-radio-group v-model="form.specialGroup" size="small">
          <el-radio-button label="none">{{ $t('text.none') }}</el-radio-button>
          <el-radio-button label="trash">{{ $t('text.trash') }}</el-radio-button>
          <el-radio-button label="backup">{{ $t('text.backup') }}</el-radio-button>
        </el-radio-group>
      </el-form-item>
      <el-divider content-position="left">{{ $t('text.thresholdInformation') }}</el-divider>
      <el-form-item prop="maxBalance" :label="$t('text.maxBalance')">
        <el-input v-model="form.maxBalance" auto-complete="off" :placeholder="$t('message.inputMaxBalance')"/>
      </el-form-item>
      <el-form-item prop="currentBalance" :label="$t('text.currentBalance')">
        <el-input v-model="form.currentBalance" :placeholder="$t('message.inputCurrentBalance')"/>
      </el-form-item>
      <el-form-item prop="enabled" :label="$t('text.status')">
        <el-select v-model="form.enabled" :placeholder="$t('message.selectStatus')" :disabled="statusDisabled">
          <el-option :label="$t('text.enabled')" :value="true"/>
          <el-option :label="$t('text.disabled')" :value="false"/>
        </el-select>
      </el-form-item>
      <el-divider content-position="left">{{ $t('text.checkoutInformation') }}</el-divider>
      <el-form-item prop="displayName" :label="$t('text.displayName')">
        <el-input v-model="form.displayName" auto-complete="off" :placeholder="$t('message.inputCheckoutDisplayName')"/>
      </el-form-item>
      <el-form-item prop="displayField" :label="$t('text.displayField')">
        <el-radio-group v-model="form.displayField" size="small">
          <el-radio-button label="00">{{ $t('text.accountNumber') }}</el-radio-button>
          <el-radio-button label="01">{{ $t('text.iban') }}</el-radio-button>
        </el-radio-group>
      </el-form-item>
      <el-form-item prop="paymentTypes" :label="$t('text.paymentTypes')">
        <el-checkbox-group v-model="form.paymentTypes" size="small">
          <el-checkbox-button v-for="paymentType in paymentTypeLIst" :key="paymentType.value" :label="paymentType.value">
            {{ paymentType.name }}
          </el-checkbox-button>
        </el-checkbox-group>
      </el-form-item>
    </el-form>
    <div slot="footer" class="dialog-footer">
      <el-button @click="handleCancelClick">{{ $t('text.cancel') }}</el-button>
      <el-button type="primary" @click="handleConfirmClick">{{ $t('text.confirm') }}</el-button>
    </div>
  </el-dialog>
</template>

<script>
export default {
  name: "BankCardInfoEdit",
  props: {
    show: Boolean,
    value: Object,
    action: String
  },
  computed: {
    title() {
      return this.action === "add" ? this.$t('text.addCard') : this.$t('text.editCard');
    },
    editMode() {
      return this.action === "edit";
    },
    groupsDisabled() {
      switch (this.form.specialGroup) {
        case "none":
          if (this.form.groups.includes(-1) || this.form.groups.includes(-2)) {
            this.form.groups = [];
          }
          break;
        case "trash":
          this.form.groups = [-1];
          break;
        case "backup":
          this.form.groups = [-2];
          break;
      }

      return this.form.specialGroup !== "none";
    },
    statusDisabled() {
      const disabled = this.form.specialGroup !== "none";
      if (disabled) {
        switch (this.form.specialGroup) {
          case "backup":
            this.form.enabled = true;
            break;
          case "trash":
            this.form.enabled = false;
            break;
        }
      }

      return disabled;
    }
  },
  data() {
    return {
      form: this.newFormData(),
      rules: {
        name: [
          {required: true, message: this.$t("message.inputCardName"), trigger: ["change", "blur"]},
          {max: 50, message: this.$t("message.lengthMax50Error"), trigger: ["change", "blur"]}
        ],
        bankCode: [
          {required: true, message: this.$t("message.selectBankName"), trigger: ["change", "blur"]}
        ],
        accountNumber: [
          {required: true, message: this.$t("message.inputAccountNumber"), trigger: ["change", "blur"]},
          {max: 50, message: this.$t("message.lengthMax50Error"), trigger: ["change", "blur"]},
          {pattern: /^[0-9]+$/, message: this.$t("message.numericError"), trigger: ["change", "blur"]}
        ],
        iban: [
          {required: true, message: this.$t("message.inputIban"), trigger: ["change", "blur"]},
          {max: 50, message: this.$t("message.lengthMax50Error"), trigger: ["change", "blur"]},
          {pattern: /^[A-Z0-9]+$/, message: this.$t("message.upperAlphabetOrNumericError"), trigger: ["change", "blur"]}
        ],
        groups: [
          {required: true, type: "array", min: 1, message: this.$t("message.selectGroup"), trigger: "blur"}
        ],
        specialGroup: [
          {required: true, trigger: "blur"}
        ],
        maxBalance: [
          {required: true, message: this.$t("message.inputMaxBalance"), trigger: ["change", "blur"]},
          {pattern: /^\d{0,16}(\.\d{1,4})?$/, message: this.$t("message.amountError"), trigger: ["change", "blur"]}
        ],
        currentBalance: [
          {required: true, message: this.$t("message.inputCurrentBalance"), trigger: ["change", "blur"]},
          {pattern: /^\d{0,16}(\.\d{1,4})?$/, message: this.$t("message.amountError"), trigger: ["change", "blur"]},
          {validator: this.validateCurrentBalance, trigger: ["change", "blur"]}
        ],
        enabled: [
          {
            required: true,
            type: "enum",
            enum: [true, false],
            message: this.$t("message.selectStatus"),
            trigger: ["change", "blur"]
          }
        ],
        displayName: [
          {required: true, message: this.$t("message.inputCheckoutDisplayName"), trigger: ["change", "blur"]},
          {max: 50, message: this.$t("message.lengthMax50Error"), trigger: ["change", "blur"]}
        ],
        displayField: [
          {
            required: true,
            type: "enum",
            enum: ["00", "01"],
            message: this.$t("message.selectDisplayField"),
            trigger: ["change", "blur"]
          }
        ],
        paymentTypes: [
          {required: true, type: "array", min: 1, message: this.$t("message.selectPaymentTypes"), trigger: "blur"}
        ]
      },
      bankList: [],
      groupList: [],
      paymentTypeLIst: [],
      loading: null
    };
  },
  methods: {
    handleDialogOpen() {
      if (this.action === "edit") {
        this.form = Object.assign({}, this.value);
        console.log(this.form);
      }
      this.refreshOptions();
    },
    handleDialogClose() {
      this.closeDialog();
    },
    handleCancelClick() {
      this.closeDialog();
    },
    handleConfirmClick() {
      this.submit();
    },
    newFormData() {
      return {
        name: null,
        bankCode: null,
        accountNumber: null,
        iban: null,
        groups: [],
        specialGroup: "none",
        maxBalance: null,
        currentBalance: null,
        enabled: null,
        displayName: null,
        displayField: null,
        paymentTypes: []
      };
    },
    closeDialog() {
      this.form = this.newFormData();
      this.$refs["form"].resetFields();
      this.$emit("update:show", false);
    },
    lockDialog() {
      this.loading = this.$loading({
        lock: true,
        text: this.$t("text.loading"),
        spinner: "el-icon-loading",
        target: "#bankCardEditor .el-dialog"
      });
    },
    unlockDialog() {
      if (this.loading !== null) {
        this.loading.close();
      }
    },
    submit() {
      this.$refs["form"].validate((valid) => {
        if (valid) {
          const url = this.action === "add" ?
              "/bankCardInfo/bankCardInfoAdd" :
              "/bankCardInfo/bankCardInfoUpdate";

          this.$axios
              .post(url, this.form)
              .then((res) => {
                if (res.code === 200) {
                  this.$notify.success({
                    title: this.$t("text.prompt"),
                    message: res.msg
                  });

                  this.$bus.$emit("refresh");
                  this.closeDialog();
                  return true;
                } else {
                  this.$notify.error({
                    title: this.$t("text.prompt"),
                    message: res.msg
                  });
                  return false;
                }
              });
        } else {
          return false;
        }
      });
    },
    refreshOptions() {
      this.$axios
          .get("/bankCardInfo/selectBankOptions", {params: {}})
          .then((res) => {
            if (res.code === 200) {
              this.bankList = res.data;
              return true;
            } else {
              this.$notify.error({
                title: this.$t("text.prompt"),
                message: res.msg
              });
              this.closeDialog();
              return false;
            }
          });

      this.$axios
          .get("/bankCardInfo/selectBankCardGroupOptions", {params: {}})
          .then((res) => {
            if (res.code === 200) {
              this.groupList = res.data;
              return true;
            } else {
              this.$notify.error({
                title: this.$t("text.prompt"),
                message: res.msg
              });
              this.closeDialog();
              return false;
            }
          });

      this.$axios
          .get("/bankCardInfo/selectPaymentTypeOptions", {params: {}})
          .then((res) => {
            if (res.code === 200) {
              this.paymentTypeLIst = res.data;
              return true;
            } else {
              this.$notify.error({
                title: this.$t("text.prompt"),
                message: res.msg
              });
              this.closeDialog();
              return false;
            }
          });
    },
    validateCurrentBalance(rule, value, callback) {
      if (this.form.maxBalance === null || this.form.maxBalance === "") {
        callback();
        return;
      }

      // 废卡不验证水位
      if (this.form.specialGroup === "trash") {
        callback();
        return;
      }

      const current = Number(this.form.currentBalance);
      const max = Number(this.form.maxBalance);
      if (isNaN(current) || isNaN(max) || current <= max) {
        callback();
      } else {
        callback(new Error(this.$t("message.currentBalanceGreaterThanMaxBalanceError")));
      }
    }
  },
  created() {
    this.refreshOptions();
  }
};
</script>

<style scoped>
.el-select {
  width: 100%;
}
</style>